// src/pages/UserApprovals.js
import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogActions,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'

const toIsoEndOfDay = (yyyyMmDd) =>
  yyyyMmDd ? dayjs(yyyyMmDd).endOf('day').toISOString() : null

const fmtYmd = (iso) => (iso ? dayjs(iso).format('YYYY-MM-DD') : '')

export default function UserApprovals() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showPendingOnly, setShowPendingOnly] = useState(false)
  const [dialog, setDialog] = useState({ open: false, action: '', user: null })
  const [isAdmin, setIsAdmin] = useState(false)
  const [meChecked, setMeChecked] = useState(false)

  // --- admin gate ---
  const checkAdmin = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setIsAdmin(false)
      setMeChecked(true)
      return
    }
    const now = new Date().toISOString()
    const { data: me } = await supabase
      .from('approved_users')
      .select('role, is_approved, expires_at')
      .eq('user_id', user.id)
      .maybeSingle()
    const admin =
      !!me &&
      me.role === 'admin' &&
      me.is_approved === true &&
      (!me.expires_at || me.expires_at >= now)
    setIsAdmin(admin)
    setMeChecked(true)
  }, [])

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError('')

    let query = supabase
      .from('approved_users')
      .select('*')
      .order('created_at', { ascending: false })
    if (showPendingOnly) query = query.eq('is_approved', false)

    const { data, error } = await query
    if (error) {
      console.error('Error fetching users:', error)
      setError('Failed to fetch users')
    } else {
      setUsers(data || [])
    }

    setLoading(false)
  }, [showPendingOnly])

  useEffect(() => {
    checkAdmin()
  }, [checkAdmin])

  useEffect(() => {
    if (isAdmin) fetchUsers()
  }, [isAdmin, fetchUsers])

  const updateUser = async (user_id, updates) => {
    const { error } = await supabase.from('approved_users').update(updates).eq('user_id', user_id)
    if (error) {
      alert(error.message || 'Update failed')
      console.error(error)
    } else {
      fetchUsers()
    }
  }

  const deleteUser = async (user_id) => {
    const { error } = await supabase.from('approved_users').delete().eq('user_id', user_id)
    if (error) {
      alert(error.message || 'Delete failed')
      console.error(error)
    } else {
      fetchUsers()
    }
  }

  const handleConfirm = async () => {
    if (!dialog.user) return
    const u = dialog.user

    if (dialog.action === 'approve') {
      const now = new Date().toISOString()
      const expires = dayjs(now)
        .add(Number(u.allowed_days || 30), 'day')
        .toISOString()
      await updateUser(u.user_id, { is_approved: true, approved_at: now, expires_at: expires })
    } else if (dialog.action === 'delete') {
      await deleteUser(u.user_id)
    }
    setDialog({ open: false, action: '', user: null })
  }

  // Inline role change (writes immediately)
  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return
    // Prevent demoting yourself accidentally (soft guard; RLS should still protect)
    const { data: { user } } = await supabase.auth.getUser()
    if (user && u.user_id === user.id && newRole !== 'admin') {
      if (!window.confirm('You are changing your own role. Continue?')) return
    }
    await updateUser(u.user_id, { role: newRole })
  }

  // Expiry helpers
  const setExpiry = async (u, ymd) => {
    await updateUser(u.user_id, { expires_at: toIsoEndOfDay(ymd) })
  }
  const clearExpiry = async (u) => {
    await updateUser(u.user_id, { expires_at: null })
  }
  const extendExpiry = async (u, days) => {
    const base = u.expires_at ? dayjs(u.expires_at) : dayjs() // if none, start from now
    const newIso = base.add(days, 'day').endOf('day').toISOString()
    await updateUser(u.user_id, { expires_at: newIso })
  }

  const handleExport = () => {
    const now = new Date().toISOString()
    const exportData = users.map(user => ({
      Email: user.email,
      Name: user.name || '-',
      Role: user.role,
      Approved: user.is_approved ? 'Yes' : 'No',
      Status:
        user.is_approved
          ? (!user.expires_at || user.expires_at >= now ? 'Active' : 'Expired')
          : 'Pending',
      'Allowed Days': user.allowed_days ?? '',
      'Approved At': user.approved_at ? dayjs(user.approved_at).format('YYYY-MM-DD HH:mm') : '',
      'Expiry Date': user.expires_at ? dayjs(user.expires_at).format('YYYY-MM-DD') : '',
    }))
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users')
    XLSX.writeFile(workbook, 'User_Approvals.xlsx')
  }

  if (!meChecked) {
    return (
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          You do not have permission to manage approvals.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>User Management</Typography>

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <FormControlLabel
          control={<Switch checked={showPendingOnly} onChange={e => setShowPendingOnly(e.target.checked)} />}
          label="Show Pending Only"
        />
        <Button variant="outlined" onClick={handleExport}>Export to Excel</Button>
        <Button variant="text" onClick={fetchUsers}>Refresh</Button>
      </Box>

      {loading ? (
        <Box sx={{ mt: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No users found</TableCell>
                </TableRow>
              ) : (
                users.map(u => {
                  const now = new Date().toISOString()
                  const statusChip = !u.is_approved
                    ? <Chip size="small" label="Pending" color="warning" />
                    : (!u.expires_at || u.expires_at >= now
                        ? <Chip size="small" label="Active" color="success" />
                        : <Chip size="small" label="Expired" color="error" />)

                  return (
                    <TableRow key={u.user_id}>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.name || '-'}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          select size="small" value={u.role}
                          onChange={(e) => changeRole(u, e.target.value)}
                          fullWidth
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>{statusChip}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            type="date"
                            size="small"
                            value={fmtYmd(u.expires_at)}
                            onChange={(e) => setExpiry(u, e.target.value)}
                          />
                          <Tooltip title="Clear expiry"><Button size="small" onClick={() => clearExpiry(u)}>Clear</Button></Tooltip>
                          <Tooltip title="Extend 30 days"><Button size="small" onClick={() => extendExpiry(u, 30)}>+30d</Button></Tooltip>
                          <Tooltip title="Extend 90 days"><Button size="small" onClick={() => extendExpiry(u, 90)}>+90d</Button></Tooltip>
                          <Tooltip title="Extend 365 days"><Button size="small" onClick={() => extendExpiry(u, 365)}>+365d</Button></Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {!u.is_approved && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setDialog({ open: true, action: 'approve', user: u })}
                            sx={{ mr: 1 }}
                          >
                            Approve
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => setDialog({ open: true, action: 'delete', user: u })}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, action: '', user: null })}>
        <DialogTitle>
          {dialog.action === 'approve' && `Approve user ${dialog.user?.email}?`}
          {dialog.action === 'delete' && `Delete user ${dialog.user?.email}?`}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, action: '', user: null })}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}